<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

import { bottomMenuToggled } from "@/analytics/events/menu";
import { trackEvent } from "@/analytics/track";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import InfoMarker from "@/components/ui/InfoMarker/InfoMarker.vue";
import { useDrawStore } from "@/store/draw";

// By default, the menu group is expanded
const props = defineProps<{ startCollapsed?: boolean; showMarker?: boolean }>();

const expandButtonRef = useTemplateRef("expandButton");
const collapseButtonRef = useTemplateRef("collapseButton");

const collapsed = ref(props.startCollapsed);

const drawMode = computed(() => useDrawStore().active);

// Expand the menu group when in draw-mode
watch(drawMode, () => {
  if (drawMode.value) {
    collapsed.value = false;
  }
});

/**
 * Expand/collapse the menu group.
 * Should be called only when the user explicitly clicks the expand/collapse button
 * (focus will be set on the trigger button)
 */
const setCollapsed = async (collapse: boolean) => {
  collapsed.value = collapse;
  trackEvent(bottomMenuToggled(!collapse));

  // Focus the 'new' trigger button
  await nextTick();
  if (collapse) {
    expandButtonRef.value?.focus();
  } else {
    collapseButtonRef.value?.focus();
  }
};

const showTrigger = computed(() => !drawMode.value);
</script>

<template>
  <div class="collapsable-menu-group">
    <Transition name="trigger">
      <InfoMarker v-if="collapsed && showMarker" :top="4" :right="4" />
    </Transition>
    <Transition name="trigger">
      <IconButton
        v-if="collapsed"
        ref="expandButton"
        icon="arrow/chevron-left"
        :text="$t('bottomMenu.expand')"
        class="expand-button"
        aria-expanded="false"
        @click="setCollapsed(false)"
      />
    </Transition>
    <Transition name="content">
      <div v-if="!collapsed" class="menu-group-inner">
        <slot />
        <!-- Trigger (hidden in draw mode) -->
        <TransitionGroup name="slide-right">
          <MenuItemDelimiter v-if="showTrigger" class="delimiter" />
          <IconButton
            v-if="showTrigger"
            ref="collapseButton"
            icon="arrow/chevron-right"
            aria-expanded="true"
            :text="$t('bottomMenu.collapse')"
            @click="setCollapsed(true)"
          />
        </TransitionGroup>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables";

$transition-time: 0.3s;

.collapsable-menu-group {
  position: relative;

  // Transition for the 'expand' button
  // -> just opacity
  // -> position absolute during transition so the group's size doesn't expand
  .trigger-enter-active,
  .trigger-leave-active {
    transition: all ease-in-out $transition-time;
    position: absolute;
  }

  .trigger-enter-from,
  .trigger-leave-to {
    opacity: 0;
  }

  .trigger-enter-to,
  .trigger-leave-from {
    opacity: 1;
  }

  // Transition for the content
  // -> max-width (from 1 button to up-to-10 buttons)
  .content-enter-active,
  .content-leave-active {
    transition: all ease-in-out $transition-time;
    overflow: hidden;
  }

  .content-enter-from,
  .content-leave-to {
    max-width: variables.$bottom-menu-button-size;
    opacity: 0;
  }

  .content-enter-to,
  .content-leave-from {
    max-width: calc(10 * variables.$bottom-menu-button-size);
    opacity: 1;
  }
}
</style>
